/**
 *  @class
 *  @function PredictiveSearch
 *  Drives the predictive (quick) search. Works for the header dropdown
 *  (with a toggle button) and the always-visible mobile search bar (inline).
 */
class PredictiveSearch {
  constructor(options) {
    this.container = options.container;

    if (!this.container) {
      return;
    }

    this.inline = options.inline || false;
    this.button = options.button ? document.getElementById(options.button) : null;
    this.close_button = this.container.querySelector('.thb-search-close');
    this.trigger = this.container.querySelector('.thb-mobile-search--trigger');
    this.back_button = this.container.querySelector('.thb-mobile-search--back');
    this.viewAll = this.container.querySelector('.thb-mobile-search--viewall');
    this.input = this.container.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.container.querySelector(options.resultsSelector);

    if (!this.input || !this.predictiveSearchResults) {
      return;
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.container.querySelector('form.searchform');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    // Header dropdown toggle button (desktop / icon).
    if (this.button) {
      this.button.addEventListener('click', (event) => {
        var _this = this;
        event.preventDefault();
        document.querySelector('.header-section').classList.toggle('search-open');

        if (document.querySelector('.header-section').classList.contains('search-open')) {
          setTimeout(function () {
            _this.input.focus({
              preventScroll: true
            });
          }, 100);
          dispatchCustomEvent('search:open');
        }

        return false;
      });
    }

    // Close button (header dropdown).
    if (this.close_button) {
      this.close_button.addEventListener('click', (event) => {
        this.close();
        event.preventDefault();
      });
    }

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') {
        if (this.inline) {
          this.closeDropdown();
        } else {
          this.close();
        }
      }
    });

    // Inline (mobile) bar. A trigger button opens a full-screen overlay that
    // holds the real input; closed via the back button or Escape.
    if (this.inline) {
      if (this.trigger) {
        this.trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.openDropdown();
          // focus() must run inside the user gesture so iOS opens the keyboard
          this.input.focus();
          this.updateViewAll();
        });
      }
      this.input.addEventListener('focus', this.onFocus.bind(this));
      if (this.back_button) {
        this.back_button.addEventListener('click', (event) => {
          event.preventDefault();
          this.input.blur();
          this.input.value = '';
          this.container.classList.remove('has-query');
          this.closeDropdown();
        });
      }
      this.updateViewAll();
    }
  }

  updateViewAll() {
    if (!this.viewAll) {
      return;
    }
    if (!this.viewAllBase) {
      this.viewAllBase = (this.viewAll.getAttribute('href') || '/search').split('?')[0];
    }
    const term = this.getQuery();
    if (term.length) {
      this.viewAll.href = `${this.viewAllBase}?q=${encodeURIComponent(term)}&type=product,collection,article,page`;
    } else {
      this.viewAll.href = this.viewAllBase;
    }
  }

  openDropdown() {
    this.container.classList.add('is-open');
    this.openedAt = Date.now();
  }

  closeDropdown() {
    this.container.classList.remove('is-open');
  }

  categoryToggle() {
    this.category_toggle = this.predictiveSearchResults.querySelectorAll('.search-categories a');

    this.category_toggle.forEach((link) => {
      link.addEventListener('click', (event) => {
        [].forEach.call(this.category_toggle, function (el) {
          el.classList.remove('active');
        });
        link.classList.add('active');
        let target = link.getAttribute('href');
        this.predictiveSearchResults.querySelectorAll('.search-results').forEach((section) => {
          section.classList.remove('active');
        });
        const targetEl = this.predictiveSearchResults.querySelector(target);
        if (targetEl) {
          targetEl.classList.add('active');
        }

        event.preventDefault();
      });
    });

  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (this.inline) {
      this.openDropdown();
      this.updateViewAll();
    }

    if (!searchTerm.length) {
      this.predictiveSearchResults.classList.remove('active');
      if (this.inline) {
        this.container.classList.remove('has-query');
      }
      return;
    }

    if (this.inline) {
      this.container.classList.add('has-query');
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (this.inline) {
      this.openDropdown();
      this.updateViewAll();
      if (searchTerm.length) {
        this.container.classList.add('has-query');
        this.getSearchResults(searchTerm);
      } else {
        this.container.classList.remove('has-query');
      }
      return;
    }

    if (!searchTerm.length) {
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    this.predictiveSearchResults.classList.add('loading');

    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,collection,article,query&${encodeURIComponent('resources[limit]')}=8&section_id=predictive-search`)
      .then((response) => {
        this.predictiveSearchResults.classList.remove('loading');
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    this.predictiveSearchResults.classList.add('active');

    this.categoryToggle();
  }

  open() {
    document.querySelector('.header-section').classList.add('search-open');
  }

  close() {
    document.querySelector('.header-section').classList.remove('search-open');
  }
}
window.addEventListener('load', () => {
  if (typeof PredictiveSearch !== 'undefined') {
    // Header dropdown (icon-triggered).
    if (document.querySelector('.thb-quick-search')) {
      new PredictiveSearch({
        container: document.querySelector('.thb-quick-search'),
        button: 'quick-search',
        resultsSelector: '.thb-quick-search--results'
      });
    }

    // Always-visible mobile search bar.
    if (document.querySelector('.thb-mobile-search')) {
      new PredictiveSearch({
        container: document.querySelector('.thb-mobile-search'),
        resultsSelector: '.thb-mobile-search--results',
        inline: true
      });
    }
  }
});
